import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../Button/button';
import './TabContent.css';
import AlertPopup from './AlertPopup';

const MessagesTab = ({ userDetails }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showNewMessage, setShowNewMessage] = useState(false);
    const [newMessage, setNewMessage] = useState({
        receiver_id: '',
        subject: '',
        content: ''
    });
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState('all');
    const [alert, setAlert] = useState({ message: '', type: 'info' });

    useEffect(() => {
        if (!userDetails?.id) return;

        // Fetch all users for the dropdown
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/users`).then(res => {
            setUsers(res.data);
        });

        const fetchMessages = async () => {
            try {
                let url = `${process.env.REACT_APP_API_BASE_URL}/message/${userDetails.id}`;
                if (filter === 'unread') {
                    url += '?is_read=false';
                }
                const response = await axios.get(url);
                setMessages(response.data);
                if (response.data.length > 0) setSelectedMessage(response.data[0]);
            } catch (error) {
                console.error('Error fetching messages:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [userDetails, filter]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/message`, {
                ...newMessage,
                sender_id: userDetails.id
            });
            setNewMessage({ receiver_id: '', subject: '', content: '' });
            setShowNewMessage(false);
            setAlert({ message: 'Message sent successfully!', type: 'success' });
        } catch (error) {
            console.error('Error sending message:', error);
            setAlert({ message: 'Error sending message', type: 'error' });
        }
    };

    const handleSelectMessage = async (message) => {
        setSelectedMessage(message);
        if (!message.is_read) {
            try {
                await axios.patch(`${process.env.REACT_APP_API_BASE_URL}/message/${message.id}/read`);
                setMessages((msgs) => msgs.map(m => m.id === message.id ? { ...m, is_read: true } : m));
            } catch (err) {
                console.error('Failed to mark as read', err);
            }
        }
    };

    if (loading) {
        return (
            <div className="tab-content">
                <div className="loaderContainer">
                    <div className="loader"></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <AlertPopup
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ message: '', type: 'info' })}
            />
            <div className="tab-content" style={{ minHeight: 'calc(100vh - 85px - 4rem)' }}>
                <div className="tab-header" style={{ borderBottom: 'none', marginBottom: 0 }}>
                    <h2 style={{ fontWeight: 700, fontSize: '2.1rem', color: '#222', letterSpacing: '-1px' }}>Messages Inbox</h2>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 900, margin: '0 auto' }}>
                        <div style={{ display: 'flex', gap: 18, fontWeight: 600, fontSize: '1.1rem', marginTop: 8 }}>
                            <span
                                style={{ color: filter === 'all' ? '#9c70f3' : '#bbb', borderBottom: filter === 'all' ? '2px solid #9c70f3' : 'none', cursor: 'pointer' }}
                                onClick={() => setFilter('all')}
                            >All Messages</span>
                            <span
                                style={{ color: filter === 'unread' ? '#9c70f3' : '#bbb', borderBottom: filter === 'unread' ? '2px solid #9c70f3' : 'none', cursor: 'pointer' }}
                                onClick={() => setFilter('unread')}
                            >Unread</span>
                        </div>
                        <Button buttonStyle="btn--primary" buttonSize="btn--small" style={{ marginLeft: 12 }} onClick={() => setShowNewMessage(true)}>
                            + New Message
                        </Button>
                    </div>
                </div>

                <div style={{ display: 'flex', width: '100%', maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(38,168,153,0.07), 0 1.5px 6px rgba(0,0,0,0.03)', minHeight: 400, marginTop: 32 }}>
                    {/* Left: Message List */}
                    <div style={{ flex: '0 0 260px', borderRight: '2px solid #eee', padding: '1.2rem 0.7rem', background: '#f8fafc', borderTopLeftRadius: 16, borderBottomLeftRadius: 16, minHeight: 400 }}>
                        <div style={{ fontWeight: 600, color: '#888', fontSize: '1.08rem', marginBottom: 12 }}>Inbox</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {messages.length > 0 ? (
                                messages.map((message, idx) => (
                                    <div
                                        key={idx}
                                        className={
                                            (selectedMessage && selectedMessage.id === message.id ? 'message-list-item selected ' : 'message-list-item ') +
                                            (!message.is_read ? 'unread-message' : '')
                                        }
                                        style={{
                                            background: selectedMessage && selectedMessage.id === message.id ? '#e8f5f3' : !message.is_read ? '#f3eaff' : 'transparent',
                                            borderRadius: 8,
                                            padding: '0.7rem 0.9rem',
                                            cursor: 'pointer',
                                            fontWeight: !message.is_read ? 700 : (selectedMessage && selectedMessage.id === message.id ? 700 : 500),
                                            color: '#333',
                                            boxShadow: selectedMessage && selectedMessage.id === message.id ? '0 2px 8px rgba(38,168,153,0.07)' : 'none',
                                            border: selectedMessage && selectedMessage.id === message.id ? '1.5px solid #9c70f3' : '1.5px solid transparent',
                                            marginBottom: 2
                                        }}
                                        onClick={() => handleSelectMessage(message)}
                                    >
                                        <div style={{ fontSize: '1.01rem', marginBottom: 2 }}>{message.subject}</div>
                                        <div style={{ fontSize: '0.93rem', color: '#888' }}>
                                            {message.sender ? `${message.sender.firstname} ${message.sender.lastname}` : message.sender_id}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#bbb', marginTop: 2 }}>{new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#bbb', fontSize: '1.05rem', marginTop: 24 }}>No messages found.</div>
                            )}
                        </div>
                    </div>
                    {/* Right: Message Details */}
                    <div style={{ flex: 1, padding: '2rem 2.2rem', borderTopRightRadius: 16, borderBottomRightRadius: 16, minHeight: 400, display: 'flex', flexDirection: 'column', background: '#fcfcff' }}>
                        {selectedMessage ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <div style={{ fontWeight: 600, color: '#888', fontSize: '1.01rem' }}>
                                        From: {selectedMessage.sender ? `${selectedMessage.sender.firstname} ${selectedMessage.sender.lastname} (${selectedMessage.sender.email})` : selectedMessage.sender_id}
                                    </div>
                                    <div style={{ color: '#bbb', fontSize: '0.98rem' }}>
                                        {new Date(selectedMessage.createdAt).toLocaleDateString()} &nbsp;|&nbsp; {new Date(selectedMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                                <div style={{ fontWeight: 700, fontSize: '1.18rem', color: '#222', marginBottom: 10 }}>{selectedMessage.subject}</div>
                                <div style={{ color: '#444', fontSize: '1.05rem', lineHeight: 1.7, background: '#fff', borderRadius: 10, padding: '1.2rem 1.3rem', boxShadow: '0 1px 6px rgba(38,168,153,0.04)' }}>
                                    {selectedMessage.content}
                                    {console.log(selectedMessage.url)}
                                    {selectedMessage.url != "" ? <a href={selectedMessage.url} style={{ pointerEvents: "auto", color: "blue" }}>Click here</a>: <></>}
                                </div>
                            </>
                        ) : (
                            <div style={{ color: '#bbb', fontSize: '1.1rem', margin: 'auto' }}>Select a message to view details.</div>
                        )}
                    </div>
                </div>

                {/* New Message Modal/Section */}
                {showNewMessage && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.18)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(38,168,153,0.13)', padding: '2.5rem 2.5rem 2rem 2.5rem', minWidth: 380, maxWidth: 420, width: '100%', position: 'relative' }}>
                            <button
                                onClick={() => setShowNewMessage(false)}
                                style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 22, color: '#bbb', cursor: 'pointer' }}
                                title="Close"
                            >
                                &times;
                            </button>
                            <h3 style={{ fontWeight: 700, fontSize: '1.25rem', color: '#222', marginBottom: 18 }}>New Message</h3>
                            <form onSubmit={handleSendMessage} className="message-form">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label">To</label>
                                        <select
                                            className="form-control"
                                            value={newMessage.receiver_id}
                                            onChange={e => setNewMessage({ ...newMessage, receiver_id: e.target.value })}
                                            required
                                        >
                                            <option value="">Select recipient</option>
                                            {users.filter(u => u.id !== userDetails.id).map(u => (
                                                <option key={u.id} value={u.id}>{u.firstname} {u.lastname} ({u.email})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="row g-3 mt-3">
                                    <div className="col-12">
                                        <label className="form-label">Subject</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newMessage.subject}
                                            onChange={e => setNewMessage({ ...newMessage, subject: e.target.value })}
                                            placeholder="Enter message subject"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="row g-3 mt-3">
                                    <div className="col-12">
                                        <label className="form-label">Message</label>
                                        <textarea
                                            className="form-control"
                                            value={newMessage.content}
                                            onChange={e => setNewMessage({ ...newMessage, content: e.target.value })}
                                            placeholder="Enter your message"
                                            required
                                            rows="4"
                                        />
                                    </div>
                                </div>
                                <div className="d-flex justify-content-end mt-4">
                                    <Button type="submit" buttonStyle="btn--primary">Send Message</Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MessagesTab; 